import React, { useState, useEffect, useMemo } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { BreakdownTable } from "./BreakdownTable";
import {
  Empty,
  message,
  Popconfirm,
  Select,
  Checkbox,
  Row,
  Col,
  Typography,
  DatePicker,
  Space,
  Button,
  Divider
} from "antd";
import { FileTwoTone } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import moment from "moment";
import { Navbar } from "../Navigation/Navbar";
import { downloadBlob } from "../../Utils/pdf_utils";
import { useSelector, useDispatch } from "react-redux";
import {
  getBreakdownData,
  setBreakdownFilterDate,
  setBreakdownFilterDrills,
  setBreakdownFilterPlayerGroups,
  setBreakdownFilterPosition,
  setBreakdownFilterSession,
} from "../../store/actions/breakdown.actions";
import { getDrills } from "../../store/actions/drills.actions";
import { getYearDataV2 } from "../../store/actions/calendar.actions";
import { LoadingComponent } from "../Common/LoadingComponent";
import { HelpButton } from "../Common/HelpButton";
import { DataColorUtil } from "../../Utils/color_utils";

const { Option } = Select;
const { Text } = Typography;

const Breakdown = () => {
  const dispatch = useDispatch();

  const trainingAPI = new TrainingAPI();

  const { date } = useParams();

  const {
    positions,
    playerGroups: allPlayerGroups,
    info: {
      hideDistanceZones,
      display_name: teamName,
      sessions: sessionList,
      team_colors: teamColors,
      coach_list
    },
    drills: drillList,
    sport,
    color_strategies: colorStrategies
  } = useSelector(({ team }) => team);

  const { data: calendarData } = useSelector(({ calendar }) => calendar);

  const {
    filter: {
      position,
      playerGroups,
      session,
      date: selectedDate,
      drills: selectedDrills,
    },
    loading,
    data,
  } = useSelector(({ breakdown }) => breakdown);

  const { drills } = useSelector(({ drills }) => drills);

  const [exportPreview, setExportPreview] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [selectedEmails, setSelectedEmails] = useState({});
  const [dataColorUtil, setDataColorUtil] = useState(null);

  const drillsOnSelectedDay = useMemo(
    () =>
      drillList.filter(
        (drill) =>
          calendarData &&
          calendarData[selectedDate] &&
          calendarData[selectedDate].drills?.includes(drill.name)
      ),
    [drillList, selectedDate, calendarData]
  );

  const fetchCalendarData = (date = selectedDate) => {
    const dateToFetch = moment(date, "YYYY-MM-DD").format('MM/DD/YYYY')
    dispatch(getYearDataV2(dateToFetch, dateToFetch));
  };

  useEffect(() => {
    if (date) {
      dispatch(setBreakdownFilterDate(date !== "" ? date : null));
      get();
    }

    trainingAPI
      .getCurrentUserData()
      .then((x) => setCurrentUserEmail({ currentUserEmail: x.email }));

    dispatch(getDrills());
    if (calendarData === null) fetchCalendarData();
  }, []);

  useEffect(() => {
    if (data?.colors && data?.player_colors && data?.metrics?.length && colorStrategies?.length) {
      setDataColorUtil(new DataColorUtil({
        teamName,
        gpsBaselines: data.colors,
        playerGpsBaselines: data.player_colors,
        teamGpsMetrics: data.metrics,
        colorStrategies
      }));
    } else {
      setDataColorUtil(null);
    }
  }, [data?.colors, data?.player_colors, data?.metrics, teamName, colorStrategies]);

  const get = () => {
    dispatch(getBreakdownData());
  };

  const handleEmail = () => {
    setEmailLoading(true);
    const emails = Object.keys(selectedEmails).filter(
      (key) => selectedEmails[key] === true
    );
    message.info("Sending Email")

    trainingAPI
      .emailBreakdownChart(selectedDate, position, playerGroups, session, selectedDrills, emails)
      .then(() => message.success("Email sent!"))
      .catch(() => message.error("Error sending email."));

    setEmailLoading(false);
  };

  const handleEmailSelectionChange = (email) => {
    const updatedSelectedEmails = {
      ...selectedEmails,
      [email]: !selectedEmails[email],
    }

    setSelectedEmails(updatedSelectedEmails);
  }

  const onDateChange = (date, dateString) => {
    dispatch(setBreakdownFilterDate(dateString !== "" ? dateString : null));
    fetchCalendarData(date)
  }

  const handleDownloadPDF = async () => {
    message.info("Generating PDF")
    setRendering(true);
    setExportPreview(true);

    trainingAPI
      .exportPDFBreakdownChart(selectedDate, position, playerGroups, session, selectedDrills)
      .then((data) => {
        downloadBlob(data, 'Breakdown.pdf');
        message.success("File Downloaded!")
      })
      .catch(() => message.error("Error exporting pdf."));

    setExportPreview(false);
    setRendering(false);
  };

  const setSelectedPosition = (val) =>
    dispatch(setBreakdownFilterPosition(val));
  const setSelectedPlayerGroups = (val) => {
    dispatch(setBreakdownFilterPlayerGroups(val));
  }
  const setSelectedSession = (val) => dispatch(setBreakdownFilterSession(val));
  const setSelectedDrills = (drills) => {
    dispatch(setBreakdownFilterDrills(drills));
    get()
  }

  const onlyGame = () => {
    const drillsOfInterest = ["Pre Game Warm Up", "First Period", "Second Period", "Third Period", "Period1", "Period2", "Period3", "Game"]
    const drillIdsOfInterest = drillsOnSelectedDay.filter(e => drillsOfInterest.includes(e.name)).map(e => e.drill_id)

    setSelectedDrills(drillIdsOfInterest)
    get()
  }

  return (
    <LoadingComponent loading={loading}>
      <Navbar />
      <HelpButton page="Breakdown" />
      <div className="search-divider"></div>
      <div className="datapick-buttons">
        <Space style={{ alignItems: 'flex-start' }}>
          <DatePicker
            onChange={onDateChange}
            value={moment(selectedDate)}
            allowClear={false}
          />
          {true ? (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <Select
                showSearch
                optionFilterProp="value"
                allowClear
                placeholder="Full Team"
                mode="multiple"
                onChange={setSelectedPosition}
                style={{ width: 150, marginLeft: "0.5em" }}
                defaultValue={position}
              >
                {positions.map((pos, i) => (
                  <Option key={i} value={pos}>
                    {pos}
                  </Option>
                ))}
              </Select>
              <Select
                allowClear
                showSearch
                optionFilterProp="value"
                placeholder="All Groups"
                value={playerGroups}
                style={{ width: 150, marginLeft: "0.5em" }}
                onChange={setSelectedPlayerGroups}
                mode="multiple"
              >
                {allPlayerGroups.map((playerGroup, i) => (
                  <Option key={i} value={playerGroup?.player_group_id}>
                    {playerGroup?.name}
                  </Option>
                ))}
              </Select>
              <Select
                allowClear
                showSearch
                optionFilterProp="value"
                placeholder="All Sessions"
                value={session}
                style={{ width: 150, marginLeft: "0.5em" }}
                onChange={setSelectedSession}
              >
                {sessionList.map((session, i) => (
                  <Option key={i} value={session}>
                    {session}
                  </Option>
                ))}
              </Select>
              <div style={{ display: 'flex', flexDirection: 'column', rowGap: '8px', width: 200, marginLeft: "0.5em" }}>
                <Select
                  allowClear
                  showSearch
                  placeholder="All Drills"
                  mode="multiple"
                  value={selectedDrills}
                  onChange={setSelectedDrills}
                  maxTagCount={"responsive"}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {drillsOnSelectedDay.map((drill, i) => (
                    <Option key={i} value={drill.drill_id}>
                      {drill.name}
                    </Option>
                  ))}
                </Select>

                {sport === "ice_hockey" &&
                  <Button type="primary" onClick={onlyGame}>
                    Only Game
                  </Button>
                }
              </div>
            </div>
          ) : null}
          <Button type="primary" onClick={get}>
            Search
          </Button>
          <Popconfirm
            placement="bottom"
            onConfirm={handleEmail}
            icon={<FileTwoTone />}
            okText="Yes"
            cancelText="No"
            title={
              <>
                <Text>
                  Would you like to generate a detailed report?
                  <br />
                </Text>
                <div style={{ marginTop: "0.5em" }}>
                  Please select the emails that should receive the report:
                </div>

                <Col style={{ marginTop: "0.5em" }}>
                  {coach_list
                    .filter((x) => x.email == currentUserEmail)
                    .map((item) => (
                      <Row>
                        <Checkbox
                          checked={
                            selectedEmails[item.email] || false
                          }
                          onChange={() =>
                            handleEmailSelectionChange(item.email)
                          }
                        >
                          {item.first_name} {item.last_name}{" "}
                          <span style={{ fontSize: 13 }}>
                            ({item.email})
                          </span>
                        </Checkbox>
                      </Row>
                    ))
                  }
                  {coach_list
                    .filter((x) => x.email != currentUserEmail)
                    .map((item) => (
                      <Row>
                        <Checkbox
                          checked={
                            selectedEmails[item.email] || false
                          }
                          onChange={() =>
                            handleEmailSelectionChange(item.email)
                          }
                        >
                          {item.first_name} {item.last_name}{" "}
                          <span style={{ fontSize: 13 }}>
                            ({item.email})
                          </span>
                        </Checkbox>
                      </Row>
                    ))
                  }
                </Col>
              </>
            }
          >
            <Button
              loading={emailLoading}
            >
              Email
            </Button>
          </Popconfirm>
          <Button
            loading={rendering}
            type="primary"
            onClick={handleDownloadPDF}
          >
            Export as PDF
          </Button>
        </Space>
      </div>
      <div className="data-divider">
        <Divider />
      </div>
      <div>
        {data && data.length !== 0 && data.data !== undefined ? (
          <BreakdownTable
            data={{...data, metrics: data.metrics.filter((e) => e.visible)}}
            chartData={data}
            loading={loading}
            hideDistanceZones={hideDistanceZones}
            team_name={teamName}
            showAll={exportPreview}
            team_colors={teamColors}
            dataColorUtil={dataColorUtil}
          />
        ) : (
          <Empty description="No data available for your query" />
        )}
      </div>
    </LoadingComponent>
  );
};

export default Breakdown;
